import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import api from '../utils/api';

// Components
import PageHeader from '../components/ui/PageHeader';
import BlogCard from '../components/blog/BlogCard';
import SearchBar from '../components/ui/SearchBar';
import PageTransition from '../components/ui/PageTransition';
import blogPost1 from '../assets/images/blog-post-1.svg';
import blogPost2 from '../assets/images/blog-post-2.svg';

const Blog = () => {
  const [blogs, setBlogs] = useState([]);
  const [filteredBlogs, setFilteredBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTag, setSelectedTag] = useState('');
  const [tags, setTags] = useState([]);

  useEffect(() => {
    const fetchBlogs = async () => {
      try {
        setLoading(true);
        const response = await api.get('/blogs');
        const data = response.data;

        setBlogs(data);
        setFilteredBlogs(data);

        // Extract unique tags
        const allTags = data.reduce((acc, blog) => {
          (blog.tags || []).forEach(tag => {
            if (!acc.includes(tag)) {
              acc.push(tag);
            }
          });
          return acc;
        }, []);
        setTags(allTags);

        setLoading(false);
      } catch (error) {
        setError('Failed to fetch blog posts. Please try again later.');
        setLoading(false);
      }
    };

    fetchBlogs();
  }, []);

  useEffect(() => {
    // Filter blogs based on search term and selected tag
    const filtered = blogs.filter(blog => {
      const matchesSearchTerm = blog.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        blog.excerpt.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesTag = selectedTag ? blog.tags.includes(selectedTag) : true;

      return matchesSearchTerm && matchesTag;
    });

    setFilteredBlogs(filtered);
  }, [searchTerm, selectedTag, blogs]);

  const handleSearch = (term) => {
    setSearchTerm(term);
  };

  const handleTagClick = (tag) => {
    setSelectedTag(tag === selectedTag ? '' : tag);
  };

  if (loading) return <LoadingContainer>Loading blog posts...</LoadingContainer>;
  if (error) return <ErrorContainer>{error}</ErrorContainer>;

  return (
    <PageTransition>
      <BlogContainer
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.5 }}
      >
        <PageHeader
          title="Blog"
          subtitle="Thoughts, insights, and updates from my journey"
        />

        <SearchBar onSearch={handleSearch} placeholder="Search blog posts..." />

        <TagsContainer>
          {tags.map(tag => (
            <TagButton
              key={tag}
              active={tag === selectedTag}
              onClick={() => handleTagClick(tag)}
            >
              {tag}
            </TagButton>
          ))}
        </TagsContainer>

        {selectedTag && (
          <SelectedFilter>
            Filtering by: <TagPill>{selectedTag}</TagPill>
            <ClearButton onClick={() => setSelectedTag('')}>Clear</ClearButton>
          </SelectedFilter>
        )}

        <BlogGrid>
          {filteredBlogs.length > 0 ? (
            filteredBlogs.map(post => (
              <BlogCard key={post._id || post.id} post={post} />
            ))
          ) : (
            <NoBlogs>No blog posts found matching your criteria.</NoBlogs>
          )}
        </BlogGrid>
      </BlogContainer>
    </PageTransition>
  );
};

const BlogContainer = styled(motion.div)`
  padding: 2rem;
  max-width: 1200px;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const TagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin: 1.5rem 0;
`;

const TagButton = styled.button`
  background-color: ${props => props.active ? 'var(--primary-color)' : 'var(--bg-secondary)'};
  color: ${props => props.active ? 'white' : 'var(--text-color)'};
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 30px;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: ${props => props.active ? 'var(--primary-dark)' : 'var(--bg-tertiary)'};
    transform: translateY(-2px);
  }
`;

const SelectedFilter = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 1.5rem;
  font-size: 0.9rem;
  color: var(--text-secondary);
`;

const TagPill = styled.span`
  background-color: var(--primary-color);
  color: white;
  padding: 0.3rem 0.8rem;
  border-radius: 30px;
  margin: 0 0.5rem;
`;

const ClearButton = styled.button`
  background: none;
  border: none;
  color: var(--primary-color);
  cursor: pointer;
  font-size: 0.9rem;
  text-decoration: underline;
  
  &:hover {
    color: var(--primary-dark);
  }
`;

const BlogGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 2rem;
  margin-top: 2rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 50vh;
  font-size: 1.2rem;
`;

const ErrorContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 50vh;
  color: #e74c3c;
  font-size: 1.2rem;
`;

const NoBlogs = styled.div`
  grid-column: 1 / -1;
  text-align: center;
  padding: 2rem;
  font-size: 1.2rem;
  color: #777;
`;

export default Blog;